import React from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { uid } from "react-uid";
import { RefreshIcon, StyledIconButton } from "@aureskonnect/react-ui";

import {
    generalConfigStore,
    setFiles,
    setImageKey,
    setProjectFiles,
} from "@store";

import { ResetModalWrapper } from "@components/Common/ResetModalWrapper";
import { ACCOUNT_CREATION_ID } from "@constants/index";
import { selectIsLoyaltyStep } from "@helpers/general";

import { ActionButtonItemWrapper } from "./ActionButtonItemWrapper";
import { LanguagesSelectWrapper } from "./LanguagesSelectWrapper";

export function DesignButtonsTabs(): JSX.Element {
    const { t } = useTranslation();

    const {
        project: { template, files: images },
        selectedActionsButtonsLanguage,
        isResetModalOpened,
    } = useSnapshot(generalConfigStore);

    const [designButtonsItems, setDesignButtonsItems] = React.useState<
        ActionsButtonsDesignItemType[]
    >((template.content.actionsButtons as ActionsButtonsDesignType).items);

    const isLoyaltyStep = selectIsLoyaltyStep(template.pages);

    // Le bouton "Créer un compte" (ACCOUNT_CREATION_ID) n'est affiché dans la
    // grille que lorsque l'étape fidélité est active. Il reste présent dans le
    // template (objet inchangé), seul son rendu est conditionné.
    const visibleDesignButtonsItems = isLoyaltyStep
        ? designButtonsItems
        : designButtonsItems.filter((item) => item.id !== ACCOUNT_CREATION_ID);

    const [
        localIsResetModalOpened,
        setLocalIsResetModalOpened,
    ] = React.useState<boolean>(isResetModalOpened);

    const templateDesignButtonsItems = (template.content
        .actionsButtons as ActionsButtonsDesignType).items;

    // Clé stable dérivée du contenu (ids des items) : `template` vient d'un
    // useSnapshot Valtio, sa référence change à chaque mutation du store. On
    // resynchronise donc sur le changement réel de la liste de boutons (ajout/
    // retrait de "Créer un compte"), pas sur l'identité du proxy.
    const designButtonsItemsKey = templateDesignButtonsItems
        .map((item: ActionsButtonsDesignItemType) => item.id)
        .join(",");

    // Resynchronise la liste locale quand le template change : l'initialiseur de
    // useState ne s'exécute qu'au montage, or le template (backend) — et donc le
    // bouton "Créer un compte" — peut arriver APRÈS le montage de ce composant.
    React.useEffect(() => {
        setDesignButtonsItems(templateDesignButtonsItems);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [designButtonsItemsKey]);

    function handleResetOnClickEvent() {
        const imagesArray = [...images];
        designButtonsItems
            .map((item: ActionsButtonsDesignItemType) => item.id)
            .forEach((id: string) => {
                const image = imagesArray.find((element) => element.id === id);
                if (image !== undefined) {
                    const imageIndex = imagesArray.indexOf(image);

                    const localContent = image.content as ImageItemLanguagesType;
                    if (image.id !== "08b4c907-8f40-41f9-ba6f-d75a493ed142") {
                        imagesArray[imageIndex] = {
                            ...image,
                            content: {
                                ...localContent,
                                [selectedActionsButtonsLanguage]: {
                                    ...localContent[
                                        selectedActionsButtonsLanguage
                                    ],
                                    path:
                                        localContent[
                                            selectedActionsButtonsLanguage
                                        ].defaultImage,
                                },
                            } as ImageItemLanguagesType,
                        };
                    } else {
                        imagesArray[imageIndex] = {
                            ...image,
                            content: image.defaultContent as string,
                        };
                    }
                }
            });

        setFiles(imagesArray);
        setProjectFiles(imagesArray);
        setImageKey(uid(imagesArray));
    }
    return (
        <React.Fragment>
            <div
                className="d-flex flex-column cursor__clz"
                style={{ gap: "20px" }}
            >
                <div
                    className="d-flex flex-column mt-3"
                    style={{ gap: "10px" }}
                >
                    <div
                        style={{
                            font: "normal normal 600 35px/61px Nunito Sans",
                        }}
                    >
                        {t("Action buttons")}
                    </div>
                    <div className="d-flex align-items-center justify-content-between">
                        <LanguagesSelectWrapper />
                        <span data-testid="design-buttons-tabs-reset-image-btn">
                            <StyledIconButton
                                title={t("Reset to default image")}
                                outline={true}
                                variant="danger"
                                className="m-0 p-0"
                                icon="RefreshIcon"
                                style={{ height: "20%", width: "20px" }}
                                onClick={() => {
                                    setLocalIsResetModalOpened(
                                        !localIsResetModalOpened
                                    );
                                }}
                            >
                                <RefreshIcon
                                    height={20}
                                    width={20}
                                    className="Ksk_btn_restoreTheDefaultImageActionButtons"
                                />
                            </StyledIconButton>
                        </span>
                    </div>
                </div>

                <div
                    className="d-grid__clz"
                    style={{
                        gap: "20px",
                    }}
                >
                    <div
                        className="d-flex justify-content-center"
                        style={{ gap: "80px" }}
                    >
                        {visibleDesignButtonsItems
                            .slice(0, 5)
                            .map(
                                (
                                    item: ActionsButtonsDesignItemType,
                                    index: number
                                ) => {
                                    return (
                                        <ActionButtonItemWrapper
                                            item={item}
                                            index={index}
                                            key={item.id}
                                            setItems={setDesignButtonsItems}
                                        />
                                    );
                                }
                            )}
                    </div>
                    <div
                        className="d-flex justify-content-center"
                        style={{ gap: "80px" }}
                    >
                        {visibleDesignButtonsItems
                            .slice(5)
                            .map(
                                (
                                    item: ActionsButtonsDesignItemType,
                                    index: number
                                ) => {
                                    return (
                                        <ActionButtonItemWrapper
                                            item={item}
                                            index={index}
                                            key={item.id}
                                            setItems={setDesignButtonsItems}
                                        />
                                    );
                                }
                            )}
                    </div>
                </div>
            </div>
            <ResetModalWrapper
                handleResetOnClickEvent={handleResetOnClickEvent}
                localIsResetModalOpened={localIsResetModalOpened}
                setLocalIsResetModalOpened={setLocalIsResetModalOpened}
            />
        </React.Fragment>
    );
}
